/**
 * Type a few letters, reach any screen — the quick-jump (ADR-0085).
 *
 * The rail answers D8 ("one click between any two Ops screens") for the screens a reader can
 * see at once; a reader holding every responsibility is offered twenty-five links under eight
 * headings, which is more than a laptop shows. This is the answer that does not depend on how
 * many you hold: `Ctrl`+`K`, three letters, `Enter`.
 *
 * **It searches screens, not data, and that is a scope change the plan should be read against.**
 * The approved plan says "Quick-jump over Order reference and Merchant name only". A Merchant
 * name is servable — `GET /ops/directory/merchants` exists — but **nothing in the API resolves
 * an Order reference to anything an operator can open**: `GET /ops/shipments` takes a `limit`
 * and nothing else, and there is no search, lookup or resolve route anywhere in it (checked, not
 * recalled). So that half needs a new audited cross-tenant route with its own I1 leak review,
 * which is API work this slice does not do. It is named as owed rather than half-built, and the
 * ADR carries the reason.
 *
 * What that leaves is, happily, the thing F4 actually described: *"2 clicks and a mandatory
 * round-trip through `/` between any two Ops screens"*. A navigator over the route table fixes
 * exactly that, and it is **I1-free by construction** — it indexes `SCREENS` and the i18n
 * bundles, so there is no record in it to leak and no request behind it to audit.
 *
 * **A combobox rather than a modal, and the reason is measured.** A command palette wants
 * `<dialog showModal()>` for its focus trap, Escape and inertness — all free, none hand-written.
 * jsdom 26 does not implement `showModal` (it throws), so a dialog here would be untestable
 * without a polyfill this repository does not have. A combobox needs no trap at all, because
 * focus never leaves the input, and it is the pattern ARIA defines for exactly this. Recorded
 * because slice 5 commits to native `<dialog>` for confirmations and will meet the same wall.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { SessionView } from '../api/client.js';
import { reachableScreens, type Screen } from './screens.js';

type LiveSession = Extract<SessionView, { authenticated: true }>;

/**
 * Case-folded, and no more than that.
 *
 * Arabic has several ways to write the same word — alef with and without hamza, a tatweel
 * stretched between letters — and this matches none of them to each other. The kernel's
 * `foldName` does, and is deliberately not reached for: it is pinned to the byte because a
 * screening decision rests on it (ADR-0070), and borrowing it for a search box would make a
 * convenience a reason not to change it. The limit is small here, because a reader is matching
 * a label they are looking straight at.
 */
function fold(text: string): string {
  return text.toLocaleLowerCase();
}

export function QuickJump({ session }: { readonly session: LiveSession }): React.JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  /*
   * Memoised on the session rather than on the screens it yields: `reachableScreens` builds a
   * fresh array every render, so a dependency on *that* would make this recompute every time
   * and the memo a decoration.
   */
  const screens = useMemo(() => reachableScreens(session), [session]);

  /*
   * A screen matches on its own name *or* its group's, so "money" finds the seven screens under
   * that heading without a reader knowing one of them is called "Remittance rail". The rail
   * already shows that grouping, so this is the same vocabulary reached a second way.
   */
  const matches = useMemo(() => {
    const needle = fold(query.trim());
    const named = screens.map((screen) => ({
      screen,
      haystack: fold(`${t(screen.navKey ?? screen.labelKey)} ${t(`nav.group.${screen.group}`)}`),
    }));
    if (needle.length === 0) return named.map((entry) => entry.screen);
    return named.filter((entry) => entry.haystack.includes(needle)).map((entry) => entry.screen);
  }, [query, screens, t]);

  // The highlight follows the list: a query that shortens it must not leave it past the end.
  useEffect(() => {
    setAt(0);
  }, [query]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((was) => !was);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) input.current?.focus();
    else setQuery('');
  }, [open]);

  if (screens.length === 0) return null;

  function go(screen: Screen | undefined): void {
    if (!screen) return;
    setOpen(false);
    navigate(screen.path);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setAt((was) => (matches.length === 0 ? 0 : (was + 1) % matches.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setAt((was) => (matches.length === 0 ? 0 : (was - 1 + matches.length) % matches.length));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(matches[at]);
    }
  }

  return (
    <div className="jump">
      <button
        type="button"
        className="btn jump__open"
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
      >
        {t('nav.jump.open')} <span className="mono">{t('nav.jump.shortcut')}</span>
      </button>

      {open && (
        <div className="jump__panel">
          {/*
            The combobox pattern: focus stays in the input and the list is described by
            `aria-activedescendant`, so arrowing through results never moves focus — which is
            what makes a focus trap unnecessary rather than merely absent.
          */}
          <input
            ref={input}
            className="jump__input"
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="jump-list"
            aria-autocomplete="list"
            aria-label={t('nav.jump.label')}
            aria-activedescendant={matches[at] ? `jump-${matches[at].path}` : undefined}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <ul className="jump__list" id="jump-list" role="listbox" aria-label={t('nav.jump.label')}>
            {matches.map((screen, index) => (
              <li
                key={screen.path}
                id={`jump-${screen.path}`}
                role="option"
                aria-selected={index === at}
                className="jump__option"
              >
                {/*
                  A button rather than a link: the option is already the control, and a nested
                  link would give one row two names in the accessibility tree.
                */}
                <button type="button" className="jump__go" onClick={() => go(screen)}>
                  <span>{t(screen.navKey ?? screen.labelKey)}</span>
                  <small className="muted">{t(`nav.group.${screen.group}`)}</small>
                </button>
              </li>
            ))}
          </ul>
          {matches.length === 0 && <p className="muted jump__none">{t('nav.jump.none')}</p>}
        </div>
      )}
    </div>
  );
}
